import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../Styles/PortalPolicies.scss";
import { Tooltip } from "antd";
import {
  ShieldCheck,
  FileText,
  Lock,
  Clock,
  X,
  Search,
  ChevronDown,
  Sparkles,
  Mail,
  MapPin,
  HelpCircle,
} from "lucide-react";

// Complete 20 Terms of Service Clauses + Privacy Policy + SLA Search Index
const POLICY_DATABASE = [
  // --- TERMS OF SERVICE (1 - 12) ---
  {
    id: "term-1",
    category: "terms",
    categoryLabel: "Terms of Service",
    clauseNumber: 1,
    title: "1. Definitions and Interpretation",
    summary: "Defines Agreement, Applicable Laws, Cloud, Confidential Information, Charges, and Data Controller / Data Processor roles.",
    content: `Certain terms are defined as follows:
• Agreement: refers to this document and any annexures or amendments.
• Applicable Laws: refers to any relevant legal regulations.
• Cloud: refers to cloud services provided by platforms like Microsoft Azure or Google Cloud.
• Confidential Information: includes any proprietary or sensitive data exchanged between the Parties.
• Charges: refers to the payments agreed upon for services provided.
• Data Controller: refers to the entity that controls data, while "Data Processor" refers to the one that processes it.`,
  },
  {
    id: "term-2",
    category: "terms",
    categoryLabel: "Terms of Service",
    clauseNumber: 2,
    title: "2. Scope of Services",
    summary: "iNextLabs delivers SaaS AI solutions via the internet, maintaining software to the latest versions.",
    content: `iNextLabs will provide access to its AI-powered solutions via its website, maintaining the latest versions of the software, subject to payment. The Customer is responsible for associated costs as outlined in the annexure. iNextLabs will deliver services in a SaaS model, accessible via the internet.`,
  },
  {
    id: "term-3",
    category: "terms",
    categoryLabel: "Terms of Service",
    clauseNumber: 3,
    title: "3. Payment and Pricing",
    summary: "Monthly/yearly credit card billing, auto-renewal, upgrade/downgrade rules, and 30-day plan change notice.",
    content: `• Subscription: The Customer may subscribe to services on a monthly or yearly basis. Payment is made by credit card, and subscriptions automatically renew unless canceled seven days prior to renewal.
• Payment: Payments are due in advance of each subscription period. iNextLabs reserves the right to suspend services if payment is not received by the due date. All fees are non-refundable unless stated otherwise.
• Upgrades and Downgrades: Customers can change their subscription plan at any time. Changes take effect at the next billing cycle unless otherwise agreed. Upgrading may incur additional fees, while downgrading may result in a loss of features.
• Early Termination: Early termination fees may apply as outlined in the subscription plan to compensate iNextLabs for the loss of anticipated revenue.
• Changes to Subscription Plans: iNextLabs reserves the right to modify its subscription plans. Customers will be notified of any material changes at least 30 days in advance.`,
  },
  {
    id: "term-4",
    category: "privacy",
    categoryLabel: "Data Privacy & PDPA",
    clauseNumber: 4,
    title: "4. Data Privacy & PDPA Compliance",
    summary: "Compliance with the Singapore Personal Data Protection Act 2012 (PDPA). Data processed strictly under Customer instructions.",
    content: `The Parties agree that personal data may be exchanged during service provision. Both iNextLabs and the Customer must comply with applicable data protection laws, including the Personal Data Protection Act 2012 (PDPA). iNextLabs will only process data as instructed by the Customer and will take measures to ensure data security.`,
  },
  {
    id: "term-5",
    category: "privacy",
    categoryLabel: "Data Ownership",
    clauseNumber: 5,
    title: "5. Data Ownership (100% Customer Property)",
    summary: "Customer retains 100% sole and exclusive ownership of all uploaded, created, and generated data.",
    content: `iNextLabs acknowledges that all data created, uploaded, or generated by the Customer in connection with the use of iNextLabs' services shall remain the sole and exclusive property of the Customer. iNextLabs will not claim ownership of customer data and will not access, use, or disclose customer data except as necessary to perform services, comply with legal obligations, or with the express written consent of the Customer.`,
  },
  {
    id: "term-6",
    category: "terms",
    categoryLabel: "Terms of Service",
    clauseNumber: 6,
    title: "6. Hosting Location & SaaS Infrastructure",
    summary: "Data centers selected based on geographic considerations and strict security standards.",
    content: `iNextLabs' solutions are hosted in a Software-as-a-Service (SaaS) environment. The hosting location of customer data will be determined based on geographic considerations. iNextLabs will ensure that the selected data center adheres to necessary security and legal compliance standards. Customers will be informed of the initial hosting location and notified of any changes.`,
  },
  {
    id: "term-7",
    category: "terms",
    categoryLabel: "Terms of Service",
    clauseNumber: 7,
    title: "7. Intellectual Property Rights",
    summary: "Non-exclusive license granted to Customer. iNextLabs retains core software and IP rights.",
    content: `iNextLabs retains all rights to its solutions and intellectual property. The Customer is granted a non-exclusive, non-transferable license to use the services under the terms of this Agreement. The Customer may not reverse-engineer, copy, or modify iNextLabs' software or transfer it to third parties without permission.`,
  },
  {
    id: "term-8",
    category: "terms",
    categoryLabel: "Terms of Service",
    clauseNumber: 8,
    title: "8. Indemnity and Limitation of Liability",
    summary: "Liability limited to charges paid during Customer's last billing cycle. Indirect damages excluded.",
    content: `Each Party agrees to indemnify the other for third-party claims or damages arising from breaches of the Agreement or negligence. iNextLabs' liability is limited to the charges paid during the Customer's last billing cycle. Neither Party is liable for indirect or consequential damages, such as loss of profits or business interruption.`,
  },
  {
    id: "term-9",
    category: "terms",
    categoryLabel: "Terms of Service",
    clauseNumber: 9,
    title: "9. Warranties",
    summary: "Services provided on an 'as-is' basis without express or implied warranties.",
    content: `iNextLabs provides its services on an "as-is" basis without express or implied warranties. There is no guarantee that services will be uninterrupted, secure, or error-free. The Customer uses the services at their own risk.`,
  },
  {
    id: "term-10",
    category: "terms",
    categoryLabel: "Terms of Service",
    clauseNumber: 10,
    title: "10. Governing Law and Dispute Resolution",
    summary: "Subscription duration, renewal rules, and 1-month written termination notice.",
    content: `This Agreement remains in effect for the duration of the subscription and renews automatically unless terminated. Either Party may terminate with one month’s notice. Upon termination, the Customer must return any confidential information and uninstall iNextLabs' software.`,
  },
  {
    id: "term-11",
    category: "terms",
    categoryLabel: "Terms of Service",
    clauseNumber: 11,
    title: "11. Miscellaneous & Force Majeure",
    summary: "Force majeure protections, written consent for modifications, and written notices.",
    content: `• Force Majeure: Neither Party will be held liable for delays or failures due to events beyond reasonable control, such as natural disasters or force majeure.
• Modifications: This Agreement can only be modified with the written consent of both Parties.
• Notices: Notices between the Parties must be in writing. This Agreement constitutes the entire understanding between the Parties and supersedes any prior agreements.`,
  },
  {
    id: "term-12",
    category: "privacy",
    categoryLabel: "Security & Encryption",
    clauseNumber: 12,
    title: "12. Confidentiality, Security & Encryption",
    summary: "Data encrypted in transit (TLS 1.3) and at rest (AES-256). Least privilege access and data breach notifications.",
    content: `iNextLabs is committed to maintaining the confidentiality and security of all customer data. iNextLabs shall implement and maintain comprehensive administrative, technical, and physical safeguards designed to protect customer data from unauthorized access, use, alteration, or disclosure. All customer data shall be encrypted both in transit and at rest, and access to such data shall be restricted solely to authorized personnel, in accordance with the principle of least privilege.

iNextLabs agrees to treat all customer data as confidential and shall not disclose such data to any third party, except as necessary to provide the services, to comply with legal obligations, or with the express written consent of the customer. In the event of a data breach or any unauthorized access to customer data, iNextLabs shall promptly notify the affected customer and shall take appropriate measures to mitigate any potential harm.`,
  },

  // --- PRIVACY POLICY SECTIONS ---
  {
    id: "privacy-policy-full",
    category: "privacy",
    categoryLabel: "Official Privacy Policy",
    clauseNumber: "PP-1",
    title: "Official Privacy Policy — iNextLabs Pte. Ltd.",
    summary: "Registered Company info, Cookies Policy, Personal Data collection, retention, and transfer rules.",
    content: `Company: iNextLabs Pte. Ltd., 1 Coleman Street, #10-06, The Adelphi, Singapore 179803. Email: info@inextlabs.com | Phone: +65 9240 2070.

• Personal Data Collected: Email address, First and Last Name, Phone number.
• Usage Data: IP address, browser type/version, page visit durations, device IDs, diagnostic data.
• Tracking Technologies & Cookies: Necessary/Essential Session Cookies for authentication; Cookies Policy Acceptance Persistent Cookies; Functionality Persistent Cookies for preferences; Web Beacons.
• Use of Data: Maintain service, manage accounts, perform contracts, send security communications, evaluate business transfers.
• Security: Administrative, technical, and physical safeguards, TLS/AES encryption in transit and at rest.
• Children Privacy: Service does not address anyone under 13 years of age.`,
  },

  // --- SLA & UPTIME COMMITMENTS (13 - 20) ---
  {
    id: "term-13",
    category: "sla",
    categoryLabel: "SLA & Subscriptions",
    clauseNumber: 13,
    title: "13. Subscription Model & Terms",
    summary: "Subscription terms, advance payments, 30-day non-renewal notice, and plan modification rules.",
    content: `iNextLabs offers its generative-AI solutions through a subscription-based model. Subscription terms begin on the date of subscription and continue for the specified plan length. Subscriptions automatically renew unless notice of non-renewal is provided at least 30 days before the current term ends.`,
  },
  {
    id: "term-14-15",
    category: "sla",
    categoryLabel: "Uptime SLA",
    clauseNumber: 15,
    title: "14-15. Service Level Agreement & 99.5% Uptime Commitment",
    summary: "Guaranteed 99.5% monthly uptime availability for all core generative-AI services.",
    content: `iNextLabs guarantees a minimum of 99.5% uptime availability for its generative-AI solutions, calculated on a monthly basis. Uptime is defined as the time during which services are accessible and operational, excluding scheduled maintenance, emergency maintenance, and third-party Azure/WhatsApp outages.`,
  },
  {
    id: "term-16",
    category: "sla",
    categoryLabel: "Support Tiers",
    clauseNumber: 16,
    title: "16. Support Response Times (Standard, Premium, Enterprise)",
    summary: "Standard support hours Mon–Fri 9 AM–6 PM. Enterprise 1-hour critical issue response.",
    content: `Standard support hours: Monday–Friday 9 AM–6 PM except public holidays.
• Standard Support: Response within 8 business hours for critical issues, 24 business hours for non-critical issues.
• Premium Support: Response within 4 business hours for critical issues, 12 business hours for non-critical issues.
• Enterprise Support: Response within 1 business hour for critical issues, 4 business hours for non-critical issues.
Critical issues are defined as those causing a complete loss of service or severe performance degradation.`,
  },
  {
    id: "term-17",
    category: "sla",
    categoryLabel: "Incident Management",
    clauseNumber: 17,
    title: "17. Incident Management & Communication",
    summary: "Proactive monitoring, assignment of resources, initial notification within 60 minutes.",
    content: `iNextLabs proactively monitors services to detect and respond to incidents. Actions include prompt detection, immediate resource assignment, and regular customer updates including an initial notification within 60 minutes of detection.`,
  },
  {
    id: "term-18",
    category: "sla",
    categoryLabel: "Maintenance",
    clauseNumber: 18,
    title: "18. Maintenance & Scheduled Windows",
    summary: "Scheduled maintenance during off-peak hours with 48 hours advance notice.",
    content: `Scheduled maintenance is conducted during off-peak hours with at least 48 hours prior notice (not counted as downtime). Emergency maintenance is conducted as needed to address urgent critical security vulnerabilities.`,
  },
  {
    id: "term-19",
    category: "sla",
    categoryLabel: "SLA Exclusions",
    clauseNumber: 19,
    title: "19. SLA Exclusions",
    summary: "Excludes performance issues caused by 3rd party providers (Microsoft Azure, WhatsApp), customer misuse, or force majeure.",
    content: `SLA guarantees do not apply to performance issues caused by third-party providers (Microsoft Azure, WhatsApp), customer misuse, hardware/software not provided by iNextLabs, or force majeure events.`,
  },
  {
    id: "term-20",
    category: "sla",
    categoryLabel: "Backups & Contact",
    clauseNumber: 20,
    title: "20. Backup Availability & Official Contact Information",
    summary: "Backups available upon request via support. Contact email: accounts@inextlabs.com | info@inextlabs.com.",
    content: `Backups of customer data are available upon request by contacting iNextLabs' support team in a timely manner according to agreed retention periods.
Contact Email: accounts@inextlabs.com | info@inextlabs.com | Phone: +65 9240 2070.`,
  },
];

const TermsAndConditionsModal = ({ showModal, handleAgree }) => {
  const [activeTab, setActiveTab] = useState("terms"); // 'terms' | 'privacy' | 'sla' | 'all'
  const [searchQuery, setSearchQuery] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  const handleAgreeClick = () => {
    if (isChecked) {
      handleAgree();
    }
  };

  const toggleAccordion = (id) => {
    setExpandedSection(expandedSection === id ? null : id);
  };

  // Universal Global Search across 100% of policy database
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) {
      return {
        isSearching: false,
        items: POLICY_DATABASE.filter(
          (item) => activeTab === "all" || item.category === activeTab || (activeTab === "terms" && item.category === "terms")
        ),
      };
    }

    const q = searchQuery.toLowerCase().trim();
    const matches = POLICY_DATABASE.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.summary.toLowerCase().includes(q) ||
        item.content.toLowerCase().includes(q) ||
        item.categoryLabel.toLowerCase().includes(q)
    );

    return {
      isSearching: true,
      items: matches,
    };
  }, [searchQuery, activeTab]);

  // Helper to highlight matching text in yellow
  const renderHighlightedText = (text, query) => {
    if (!query || !query.trim()) return text;
    const q = query.trim();
    const parts = text.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
    return parts.map((part, index) =>
      part.toLowerCase() === q.toLowerCase() ? (
        <mark key={index} className="search-highlight">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <AnimatePresence>
      {showModal && (
        <>
          {/* Apple Spatial Backdrop Blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="apple-spatial-backdrop"
            onClick={handleAgree}
          />

          {/* Apple Spatial Glass Modal Window */}
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 28, stiffness: 280 }}
            className="apple-spatial-modal-window"
          >
            {/* Top Spatial Ambient Light Strip */}
            <div className="spatial-light-rim" />

            {/* Header Section */}
            <div className="apple-spatial-header">
              <div className="header-badge-row">
                <div className="apple-spatial-pill-badge">
                  <Sparkles size={13} />
                  <span>iNextLabs Official Policy Center</span>
                </div>
                <div className="read-time-tag">
                  <Clock size={12} />
                  <span>~4 min read</span>
                </div>
              </div>

              <div className="header-title-row">
                <div>
                  <h2 className="apple-spatial-title">Terms of Service & Privacy Center</h2>
                  <p className="apple-spatial-subtitle">
                    iNextLabs Pte. Ltd. • Singapore Registered • Updated March 2021
                  </p>
                </div>
                <button
                  type="button"
                  className="apple-spatial-close-icon"
                  onClick={handleAgree}
                  aria-label="Close modal"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Instant Search Filter Bar */}
              <div className="apple-spatial-search-wrapper">
                <Search size={16} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search terms, privacy clauses, SLA response, encryption, accounts@inextlabs.com..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="apple-spatial-search-input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="clear-search-btn"
                    onClick={() => setSearchQuery("")}
                  >
                    Clear Search
                  </button>
                )}
              </div>
            </div>

            {/* Segmented Floating Pill Switcher */}
            <div className="apple-spatial-pill-nav">
              <div className="segmented-pill-container">
                <button
                  type="button"
                  className={`segmented-pill-btn ${activeTab === "terms" && !searchQuery ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab("terms");
                    setSearchQuery("");
                  }}
                >
                  <FileText size={14} />
                  <span>Terms (20 Clauses)</span>
                </button>
                <button
                  type="button"
                  className={`segmented-pill-btn ${activeTab === "privacy" && !searchQuery ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab("privacy");
                    setSearchQuery("");
                  }}
                >
                  <Lock size={14} />
                  <span>Privacy Policy</span>
                </button>
                <button
                  type="button"
                  className={`segmented-pill-btn ${activeTab === "sla" && !searchQuery ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab("sla");
                    setSearchQuery("");
                  }}
                >
                  <Clock size={14} />
                  <span>SLA & Uptime (99.5%)</span>
                </button>
                <button
                  type="button"
                  className={`segmented-pill-btn ${activeTab === "all" && !searchQuery ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab("all");
                    setSearchQuery("");
                  }}
                >
                  <ShieldCheck size={14} />
                  <span>All Docs</span>
                </button>
              </div>
            </div>

            {/* Main Scrollable Canvas */}
            <div className="apple-spatial-scroll-canvas" data-lenis-prevent="true">
              {/* Search Results Counter Badge */}
              {searchResults.isSearching && (
                <div className="search-results-bar">
                  <span>
                    Search Results for <strong>"{searchQuery}"</strong> — {searchResults.items.length} clause(s) found
                  </span>
                  <button
                    type="button"
                    className="reset-search-link"
                    onClick={() => setSearchQuery("")}
                  >
                    Reset Filter
                  </button>
                </div>
              )}

              {/* Empty Search Fallback */}
              {searchResults.isSearching && searchResults.items.length === 0 && (
                <div className="apple-spatial-empty-search">
                  <HelpCircle size={32} className="empty-icon" />
                  <h4>No matching policy clauses found</h4>
                  <p>Try searching for terms like "SLA", "encryption", "PDPA", "Singapore", "backups", or "refund".</p>
                  <button
                    type="button"
                    className="empty-reset-btn"
                    onClick={() => setSearchQuery("")}
                  >
                    View All Policy Clauses
                  </button>
                </div>
              )}

              {/* Render Policy Cards */}
              {searchResults.items.map((clause) => {
                const isExpanded = expandedSection === clause.id || searchResults.isSearching;
                return (
                  <div key={clause.id} className="apple-spatial-card clause-card">
                    <div
                      className="clause-card-header"
                      onClick={() => toggleAccordion(clause.id)}
                    >
                      <div className="clause-title-block">
                        <div className="clause-category-tag">{clause.categoryLabel}</div>
                        <h4 className="clause-title">
                          {renderHighlightedText(clause.title, searchQuery)}
                        </h4>
                        <p className="clause-summary">
                          {renderHighlightedText(clause.summary, searchQuery)}
                        </p>
                      </div>
                      <ChevronDown
                        size={18}
                        className={`clause-chevron ${isExpanded ? "rotated" : ""}`}
                      />
                    </div>

                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="clause-card-content"
                      >
                        <pre className="clause-text">
                          {renderHighlightedText(clause.content, searchQuery)}
                        </pre>
                      </motion.div>
                    )}
                  </div>
                );
              })}

              {/* Contact Footer Card */}
              <div className="apple-spatial-card contact-footer-card">
                <div className="contact-grid">
                  <div className="contact-item">
                    <MapPin size={16} className="contact-icon" />
                    <span><strong>iNextLabs Pte. Ltd.</strong> — 1 Coleman Street, #10-06, The Adelphi, Singapore 179803</span>
                  </div>
                  <div className="contact-item">
                    <Mail size={16} className="contact-icon" />
                    <span>General: <a href="mailto:info@inextlabs.com">info@inextlabs.com</a> | Billing & Accounts: <a href="mailto:accounts@inextlabs.com">accounts@inextlabs.com</a> | Phone: +65 9240 2070</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Ceramic Action Bar */}
            <div className="apple-spatial-action-bar">
              <div className="action-bar-left">
                <label htmlFor="termsCheckbox" className="spatial-checkbox-container">
                  <input
                    type="checkbox"
                    id="termsCheckbox"
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                    className="spatial-checkbox"
                  />
                  <span className="checkbox-custom" />
                  <span className="checkbox-text">
                    I understand & agree to the <strong>Terms</strong>, <strong>Privacy Policy</strong>, and <strong>SLA</strong>.
                  </span>
                </label>
              </div>

              <div className="action-bar-right">
                <Tooltip title={!isChecked ? "Please check the box to accept" : ""}>
                  <button
                    type="button"
                    onClick={handleAgreeClick}
                    disabled={!isChecked}
                    className="apple-spatial-cta-btn"
                  >
                    <span>Accept & Continue</span>
                  </button>
                </Tooltip>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default TermsAndConditionsModal;
